import { type Embed, factories, type IEmbedConfiguration, models, service } from 'powerbi-client'
import { getPathPrefix } from './helpers/href'
import { dispatchCustomEvent } from './helpers/event'

import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('powerbi-report')
export class PowerBIReport extends LitElement {
    static get tagName() {
        return 'powerbi-report'
    }

    @property({ attribute: 'report-id' })
    private reportId: string

    @property({ attribute: 'embed-url' })
    private embedUrl: string

    @property({ attribute: 'embed-token' })
    private embedToken: string

    @property({ attribute: 'intercept-report-link' })
    private interceptReportLink: string

    private readonly container: HTMLElement
    private readonly service: service.Service
    private embed: Embed | undefined = undefined

    constructor() {
        super()

        const shadow = this.attachShadow({ mode: 'open' })
        this.container = document.createElement('div')
        this.container.classList.add('powerbi-report-container')
        shadow.appendChild(this.container)

        const pathPrefix = getPathPrefix()

        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.type = 'text/css'
        link.href = `${pathPrefix}shared/css/zaptic-powerbi-report.css`
        shadow.appendChild(link)

        this.service = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory)
    }

    private emitAccessTokenLoadedEvent(accessToken: string) {
        this.dispatchEvent(new CustomEvent('accessTokenLoaded', { detail: { accessToken, composed: true } }))
    }

    get shouldInterceptReportLink() {
        return this.interceptReportLink == null ? null : this.interceptReportLink === 'true'
    }

    get config(): IEmbedConfiguration {
        return {
            type: 'report',
            tokenType: models.TokenType.Embed,
            settings: {
                filterPaneEnabled: false,
                layoutType: screen.width < 500 ? models.LayoutType.MobilePortrait : undefined,
                hyperlinkClickBehavior: this.shouldInterceptReportLink ? 2 : 0,
            },
            id: this.reportId,
            embedUrl: this.embedUrl,
            accessToken: this.embedToken,
        }
    }

    render() {
        const config = this.config
        if (!config.embedUrl || !config.accessToken || this.shouldInterceptReportLink == null) return
        if (!this.embed) {
            this.embed = this.service.embed(this.container, config)
            this.embed.off('loaded')
            this.embed.on('loaded', (_) => this.emitAccessTokenLoadedEvent(config.accessToken))

            this.embed.on('dataHyperlinkClicked', (event: service.ICustomEvent<{ url: string }>) => {
                const urlString = event.detail.url ?? ''
                const url = new URL(urlString)
                const matches = urlString.match(/\/r\/report\/(\d+)/)

                if (matches) {
                    const reportId = parseInt(matches[1], 10)
                    dispatchCustomEvent(this, 'report-linked-clicked', { reportId })
                    return
                }
                if (['all-reports', 'create-action', 'edit-action'].some((fragment) => url.hash.includes(fragment))) {
                    const currentUrl = new URL(window.location.href)
                    // need to unescape html entities in url because powerbi-client library escaped them
                    currentUrl.search = decodeHtmlEntities(url.search)
                    currentUrl.hash = decodeHtmlEntities(url.hash)
                    // when we push state then we replace current url without reload, but Elm does not know that url was changed and does nothing,
                    // so after that we call location.replace, which normally would reload page, but since it's the same url it does not do that, yet
                    // this time Elm knows that url was changed and dispatches UrlChange message
                    window.history.pushState(null, '', currentUrl.href)
                    window.location.replace(currentUrl.href)
                    return
                }
                window.location.href = urlString
            })
        } else {
            this.embed
                .setAccessToken(config.accessToken)
                .then((_) => this.emitAccessTokenLoadedEvent(config.accessToken))
        }
    }
}

const decodeHtmlEntities = (str: string): string => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(str, 'text/html')
    return doc.documentElement.textContent || ''
}
